import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Seo from '../ui/Seo';
import { Icon, Pill, Segmented, Select } from '../demo/parts';
import { acSnapshot, applyAlarmDelays, BUSES, clockFull, dcSnapshot, METERS, METER_ORDER, PROFILES, SCENARIOS, scopedConditions, TICK_MS, TIME_COMPRESSION } from '../demo/model';
import { DEFAULT_CRITERIA } from '../demo/assessment';
import { acSnapshotRows, dcSnapshotRows, downloadCsv } from '../demo/exports';
import { demoLink, EXPLAINER_PATH, PRODUCT_NAME, SCREENS, SCREEN_GROUPS } from '../demo/screens';
import { AcPower, Events, Harmonics } from '../demo/views-ac';
import { Tests } from '../demo/views-tests';
import { Electrical, Energy, Overview, Protection, Thermal } from '../demo/views-dc';
import { Health, Library } from '../demo/views-workspace';
import { Reports, Simulator, Site } from '../demo/views-site';
import { FeederDrawer, ProfileDialog, Toast } from '../demo/overlays';
import { site } from '../data/site';

/* ─────────────────────────────────────────────────────────────
   The SmartDC dashboard, running on a simulated data center.

   This is an application, not a page of the site: App.jsx drops the site's
   nav and footer on these routes, and the dashboard fills the window with its
   own frame, scrolling only the work area, the way an operator's tool does.

   The readings are simulated, and the chip in the top bar, the status bar and
   every export all say so. Those labels are what stop a sample reading passing
   for a measurement from a real data hall, so they stay however the rest of
   the product is presented.

   Each view has its own address so a search engine can reach it. They are all
   one component, so moving between them keeps the clock, the selections and
   the test history.
   ───────────────────────────────────────────────────────────── */

const VIEW_COMPONENTS = {
    site: Site,
    ac: AcPower,
    harmonics: Harmonics,
    events: Events,
    tests: Tests,
    overview: Overview,
    electrical: Electrical,
    thermal: Thermal,
    protection: Protection,
    energy: Energy,
    health: Health,
    library: Library,
    reports: Reports,
    simulator: Simulator
};

/* One broken view must not take the frame down with it: the navigation stays,
   and the operator can go somewhere else. */
class ViewBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    render() {
        if (!this.state.failed) return this.props.children;

        return (
            <p className="dash-note" role="alert">
                This view could not be drawn. The rest of the dashboard is still running, so choose another view or reload the page.
            </p>
        );
    }
}

const ACK_KEY = 'ses-monitoring-demo-acks';

/* Session storage can throw in a private window, and acknowledgments are only
   a convenience, so losing them is fine and breaking the page is not. */
const readAcks = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(ACK_KEY)) || {};
    } catch (e) {
        return {};
    }
};

const writeAcks = acks => {
    try {
        window.sessionStorage.setItem(ACK_KEY, JSON.stringify(acks));
    } catch (e) {
        /* Still works for this page view. */
    }
};

const VIEW_KEY = 'ses-monitoring-demo-compact';

const readCompact = () => {
    try {
        return window.localStorage.getItem(VIEW_KEY) === '1';
    } catch (e) {
        return false;
    }
};

const INITIAL_TESTS = {
    criteria: DEFAULT_CRITERIA,
    records: [],
    currentId: null,
    counter: 0,
    tab: 'demo',
    imported: null
};

const meterOptions = METER_ORDER.map(id => ({ value: id, label: METERS[id].label }));
const profileOptions = Object.values(PROFILES).map(profile => ({ value: profile.id, label: profile.label }));
const busOptions = [
    { value: 'both', label: 'Both buses' },
    ...BUSES.map(bus => ({ value: bus, label: `Bus ${bus}` }))
];

export default function MonitoringDemo() {
    const navigate = useNavigate();
    const { view: requested } = useParams();

    const screen = SCREENS.find(item => item.id === requested) || SCREENS[0];
    const View = VIEW_COMPONENTS[screen.id];

    /* ── The simulation clock ── */
    const [anchor] = useState(() => Date.now());
    const [now, setNow] = useState(anchor);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return undefined;

        setNow(Date.now());

        const timer = setInterval(() => {
            if (!document.hidden) setNow(Date.now());
        }, TICK_MS);

        return () => clearInterval(timer);
    }, [paused]);

    /* ── What the toolbar scopes ── */
    const [path, setPath] = useState('A');
    const [meter, setMeter] = useState('incomer');
    const [profile, setProfile] = useState('480');
    const [scope, setScope] = useState('both');

    /* ── Session state that outlives a change of view ── */
    const [tests, setTests] = useState(INITIAL_TESTS);
    const [acks, setAcks] = useState(readAcks);
    const [feederId, setFeederId] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const [compact, setCompact] = useState(readCompact);
    const [scenario, setScenario] = useState(null);
    const [alarmLog, setAlarmLog] = useState([]);
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);
    const workArea = useRef(null);

    const notify = useCallback(message => {
        setToast(message);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 4500);
    }, []);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const setView = useCallback(id => navigate(demoLink(id)), [navigate]);

    /* The window never scrolls here, so a change of view has to reset the work
       area itself. */
    useEffect(() => {
        setNavOpen(false);
        if (workArea.current) workArea.current.scrollTop = 0;
    }, [screen.id]);

    const acknowledge = useCallback(id => {
        setAcks(current => {
            const next = { ...current, [id]: Date.now() };
            writeAcks(next);
            return next;
        });
        notify('Acknowledged. The condition stays active.');
    }, [notify]);

    const resetAcks = useCallback(() => {
        setAcks({});
        writeAcks({});
        notify('Acknowledgments reset. The conditions stay active.');
    }, [notify]);

    const closeFeeder = useCallback(() => setFeederId(null), []);
    const closeProfile = useCallback(() => setProfileOpen(false), []);

    /* ── The two snapshots every view reads from ── */
    const alarmMemory = useRef({ seen: new Map(), started: false, active: new Set() });

    const dc = useMemo(() => {
        const memory = alarmMemory.current;
        const reading = dcSnapshot(now, anchor, scenario, memory.active);
        const conditions = applyAlarmDelays(reading.conditions, memory, now);

        memory.active = new Set(conditions.map(condition => condition.id));

        return { ...reading, conditions };
    }, [now, anchor, scenario]);
    const ac = useMemo(() => acSnapshot(now, path, meter, profile, dc), [now, path, meter, profile, dc]);

    /* The alarm log: what actually changed while this page was open. The first
       pass only learns what is already active, so opening the page logs nothing. */
    const known = useRef(null);

    useEffect(() => {
        const current = new Map(dc.conditions.map(condition => [condition.id, condition]));

        if (known.current) {
            const raised = [...current.values()].filter(condition => !known.current.has(condition.id));
            const cleared = [...known.current.values()].filter(condition => !current.has(condition.id));

            if (raised.length || cleared.length) {
                const row = (condition, state) => ({ at: dc.ms, asset: condition.asset, event: condition.title, state, severity: condition.severity });
                // An advisory replaced by a critical alarm has not recovered.
                const ending = condition => (raised.some(c => c.asset === condition.asset && c.severity === 'critical') ? 'Escalated' : 'Cleared');

                setAlarmLog(log => [...raised.map(c => row(c, 'Raised')), ...cleared.map(c => row(c, ending(c))), ...log]);
            }

            // A critical alarm stepping back down to its advisory is not news.
            const news = raised.find(condition => !cleared.some(c => c.asset === condition.asset && c.severity === 'critical'));

            if (news) {
                notify(`${news.severity === 'critical' ? 'Critical alarm' : 'New advisory'}: ${news.asset} · ${news.short}`);
            }
        }

        known.current = current;
    }, [dc, notify]);

    const startScenario = useCallback(id => {
        const startedAt = Date.now();

        setPaused(false);
        setNow(startedAt);
        setScenario({ id, startedAt });
        setAlarmLog(log => [{ at: startedAt, asset: 'Simulator', event: `Fault started: ${SCENARIOS.find(item => item.id === id).title}`, state: 'Started', severity: null }, ...log]);
    }, []);

    const stopScenario = useCallback(() => {
        const stoppedAt = Date.now();

        setNow(stoppedAt);
        setScenario(null);
        setAlarmLog(log => [{ at: stoppedAt, asset: 'Simulator', event: 'Returned to normal', state: 'Ended', severity: null }, ...log]);
        notify('Returned to normal operation.');
    }, [notify]);

    const openMeter = useCallback((bus, stage) => {
        setPath(bus);

        if (stage === 'link') {
            setScope(bus);
            navigate(demoLink('electrical'));
        } else {
            setMeter(stage);
            navigate(demoLink('ac'));
        }
    }, [navigate]);

    const toggleCompact = () => {
        setCompact(current => {
            try {
                window.localStorage.setItem(VIEW_KEY, current ? '0' : '1');
            } catch (e) {
                /* Only a convenience: it still applies to this page view. */
            }
            return !current;
        });
    };

    const sim = {
        now, anchor, dc, ac, path, meter, scope, tests, setTests, acks, acknowledge, resetAcks, notify, setView,
        setMeter,
        criteria: tests.criteria,
        openFeeder: setFeederId,
        openMeter,
        scenario,
        startScenario,
        stopScenario,
        alarmLog
    };

    const advisories = dc.conditions.length;
    const criticals = dc.conditions.filter(condition => condition.severity === 'critical').length;
    const running = scenario && SCENARIOS.find(item => item.id === scenario.id);
    const scoped = scopedConditions(dc, scope).length;
    const isAc = screen.toolbar === 'ac' || screen.toolbar === 'events';

    const exportSnapshot = () => {
        if (isAc) {
            downloadCsv(`SmartDC_Demo_AC_Path${path}_${meter}.csv`, acSnapshotRows(ac));
        } else {
            downloadCsv(`SmartDC_Demo_DC_${scope}.csv`, dcSnapshotRows(dc, scope));
        }

        notify('Snapshot exported as a CSV, marked simulated, with units.');
    };

    const togglePause = () => {
        setPaused(current => !current);
        notify(paused ? 'Telemetry resumed.' : 'Telemetry paused. All readings are held.');
    };

    return (
        <>
            <Seo
                title={`${screen.title} | ${PRODUCT_NAME}`}
                description={`${screen.text} Live ${PRODUCT_NAME} dashboard demo with simulated data.`}
                keywords="SmartDC, data center power monitoring, power quality dashboard, UPS monitoring, 800 VDC, busbar temperature monitoring, insulation monitoring"
                path={demoLink(screen.id)}
                schema={{
                    '@type': 'WebPage',
                    name: `${screen.title}: ${PRODUCT_NAME} demo`,
                    description: screen.text,
                    url: `${site.origin}${demoLink(screen.id)}`
                }}
            />

            <div className={`dash${compact ? ' dash--compact' : ''}${paused ? ' is-paused' : ''}`}>
                <header className="dash-topbar">
                    <button
                        type="button"
                        className="dash-iconbtn dash-topbar__menu"
                        aria-label="Open navigation"
                        aria-expanded={navOpen}
                        onClick={() => setNavOpen(open => !open)}
                    >
                        <Icon name="menu" />
                    </button>

                    <Link className="dash-brand" to="/" aria-label={`${site.name} home`}>
                        <img src={site.logo} alt="" width="1920" height="1080" />
                        <span>{PRODUCT_NAME}</span>
                    </Link>

                    <span className="dash-topbar__crumbs">DC-01 <i>/</i> Data hall 01 <i>/</i> <b>{screen.title}</b></span>

                    <span className="dash-topbar__end">
                        <Link className={`dash-alarm${criticals ? ' is-critical' : ''}`} to={demoLink('protection')} aria-label={`${advisories} active alarms, ${criticals} critical`}>
                            <Icon name="bell" />
                            {advisories}
                        </Link>

                        <span className="dash-topbar__clock">{clockFull(now)} <small>ET</small></span>

                        <Pill tone="info">Demo data</Pill>

                        <button
                            type="button"
                            className="dash-iconbtn"
                            onClick={togglePause}
                            aria-pressed={paused}
                            aria-label={paused ? 'Resume simulated telemetry' : 'Pause simulated telemetry'}
                            title={paused ? 'Resume' : 'Pause'}
                        >
                            <Icon name={paused ? 'play' : 'pause'} />
                        </button>

                        <button
                            type="button"
                            className="dash-iconbtn"
                            onClick={toggleCompact}
                            aria-pressed={compact}
                            aria-label={compact ? 'Show explanations under readings' : 'Hide explanations for a compact view'}
                            title={compact ? 'Show explanations' : 'Compact view'}
                        >
                            <Icon name="notes" />
                        </button>

                        <button type="button" className="dash-iconbtn" onClick={() => setProfileOpen(true)} aria-label="Open system profile" title="System profile">
                            <Icon name="sliders" />
                        </button>

                        <Link className="dash-exit" to={EXPLAINER_PATH} aria-label="Product overview" title="Product overview">
                            <span>Product overview</span>
                            <Icon name="exit" />
                        </Link>
                    </span>
                </header>

                {navOpen && <button type="button" className="dash-navscrim" aria-label="Close navigation" onClick={() => setNavOpen(false)} />}

                <aside className={`dash-side${navOpen ? ' is-open' : ''}`}>
                    <nav aria-label="Dashboard views">
                        {SCREEN_GROUPS.map(group => (
                            <div className="dash-side__group" key={group}>
                                <span className="dash-side__label">{group}</span>
                                <ul>
                                    {SCREENS.filter(item => item.group === group).map(item => (
                                        <li key={item.id}>
                                            <Link to={demoLink(item.id)} aria-current={item.id === screen.id ? 'page' : undefined}>
                                                <Icon name={item.id} />
                                                <span>{item.title}</span>
                                                {item.id === 'protection' && <span className={`dash-side__badge${criticals ? ' is-critical' : ''}`}>{advisories}</span>}
                                                {item.id === 'simulator' && running && <span className="dash-side__badge is-critical">On</span>}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <button type="button" className="dash-side__profile" onClick={() => setProfileOpen(true)}>
                        <span className="dash-side__label">System</span>
                        <strong>AC + 800 VDC UPS</strong>
                        <span>Independent A / B paths</span>
                    </button>
                </aside>

                <div className="dash-main" ref={workArea}>
                    <div className="dash-head">
                        {running && (
                            <p className="dash-scenario" role="status">
                                <span>
                                    <strong>Simulated fault running: {running.title}</strong> · {running.where} · Battery discharge runs {TIME_COMPRESSION}× faster than real time
                                </span>
                                <button type="button" className="dash-btn" onClick={stopScenario}>Return to normal</button>
                            </p>
                        )}

                        <div className="dash-head__row">
                            <h1 className="dash-title">{screen.title}</h1>

                            {isAc && (
                                <div className="dash-toolbar">
                                    <Segmented label="Power path" value={path} onChange={setPath} options={[{ value: 'A', label: 'Path A' }, { value: 'B', label: 'Path B' }]} />
                                    {screen.toolbar === 'ac' && <Select label="Meter location" value={meter} onChange={setMeter} options={meterOptions} />}
                                    <Select label="System profile" value={profile} onChange={setProfile} options={profileOptions} />
                                </div>
                            )}

                            {screen.toolbar === 'dc' && (
                                <div className="dash-toolbar">
                                    <Segmented label="Bus scope" value={scope} onChange={setScope} options={busOptions} />
                                    <Link className="dash-advisory" to={demoLink('protection')}>
                                        {scoped} active {scoped === 1 ? 'alarm' : 'alarms'}
                                    </Link>
                                </div>
                            )}

                            <div className="dash-head__actions">
                                {screen.id !== 'library' && <Link className="dash-link" to={demoLink('library')}>Measurement guide</Link>}
                                <button type="button" className="dash-btn" onClick={exportSnapshot}>
                                    <Icon name="download" />
                                    Export snapshot
                                </button>
                            </div>
                        </div>

                        <p className="dash-subtitle">{screen.subtitle}</p>

                        {isAc && (
                            <p className="dash-context">
                                {screen.toolbar === 'events' ? (
                                    <><strong>Event analysis · Path {path}</strong> · All meter locations · Timestamps in Eastern time · Simulated recorder history</>
                                ) : (
                                    <><strong>{ac.meter.label} · Path {path}</strong> · {ac.profile.label} · Three-phase wye · 3φ 4-wire · Simulated instruments · Read-only</>
                                )}
                            </p>
                        )}
                    </div>

                    <div className="dash-view">
                        <ViewBoundary key={screen.id}>
                            <View sim={sim} />
                        </ViewBoundary>
                    </div>
                </div>

                <footer className="dash-status">
                    <span className="dash-status__live">
                        <i className={paused ? 'is-paused' : undefined} aria-hidden="true" />
                        {paused ? 'Snapshot paused' : 'Simulated · 2 s refresh'}
                    </span>
                    <span className="dash-status__demo">{running ? `Simulated fault: ${running.title}. ` : ''}Simulated data. No equipment is connected.</span>
                    <Link className="dash-status__link" to={demoLink('health')}>14/14 power meters · 15/16 thermal · 2/2 insulation</Link>
                    <span className="dash-status__end">Updated {clockFull(now)} ET</span>
                </footer>
            </div>

            {feederId && <FeederDrawer sim={sim} feederId={feederId} onClose={closeFeeder} />}
            {profileOpen && <ProfileDialog sim={sim} onClose={closeProfile} />}
            <Toast message={toast} />
        </>
    );
}
